import { useElementDistribution } from '../../hooks/useElementDistribution';
import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { FundraisingBar } from 'donation-elements';
import { getAuthToken, getAuthGroups } from '../../utils/auth';
import { toast } from 'sonner';
import { Label } from '../../../components/ui/label';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import ElementEditorSection, {
  elementEditorSectionHasErrors,
  elementEditorSectionIcons,
  focusFirstInvalidElement,
} from '../workspaces/ElementEditorSection';
import '../../css/multi-select-styling.css';
import type {
  CreateFundraisingBarDrawerProps,
  FundraisingBarFormData,
  Tab,
  WorkspaceHeaderAction,
} from './types/FundraisingBarTypes';

// Import tab components
import FundraisingBarConfiguration from './tabs/FundraisingBarConfiguration';
import AppearanceTab from './tabs/AppearanceTab';

import type { FormData, ColorPickerColor } from './tabs/types';
import { copyToClipboard } from '@charitystack/shared';
import { portalClient } from '../../lib/trpc/portal';

// Utility function for classNames
function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

const tabs: Tab[] = [
  { name: 'Configuration', step: 'configuration', current: true },
  { name: 'Appearance', step: 'appearance', current: false },
];

const initialFormData: FundraisingBarFormData = {
  elementName: '',
  connectedForms: [],
  color: '',
  goal: '',
  elementOffset: '',
  trackingMetric: 'DOLLAR_AMOUNT',
  metricLabel: 'Sustainers',
};

export default function CreateFundraisingBarDrawer(props: CreateFundraisingBarDrawerProps) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  const useCreateWorkspaceCards = props.presentation === 'workspace';
  const [formData, setFormData] = useState<FundraisingBarFormData>(initialFormData);
  const [elementID, setElementID] = useState<string | null>(null);
  const [availableForms, setAvailableForms] = useState<FormData[]>([]);
  const [formsRaisedAmount, setFormsRaisedAmount] = useState<number>(0);
  const [formsSubscriptionCount, setFormsSubscriptionCount] = useState<number>(0);
  const [formsProductCount, setFormsProductCount] = useState<number>(0);
  const [formsDonationCount, setFormsDonationCount] = useState<number>(0);
  const [active, setActive] = useState<boolean>(true);
  const { canDistribute, refresh: verifyDistribution } = useElementDistribution(
    {
      ...props.elementData,
      elementID,
      active,
      elementType: 'FUNDRAISING_BAR',
      formIDs: [
        ...(props.elementData?.formIDs || []),
        ...formData.connectedForms
          .map((name) => availableForms.find((form) => form.formName === name)?.formID)
          .filter((id): id is string => !!id),
      ],
    },
    paymentsReady,
    props.open,
  );

  // Add validation states
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<string>('configuration');

  // Add loading states
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  // Color picker state
  const [sketchPickerColor, setSketchPickerColor] = useState<ColorPickerColor>({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  });
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

  // Add form reference
  const formRef = useRef<HTMLFormElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // Update formData helper
  const updateFormData = (field: keyof FundraisingBarFormData, value: any): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      newInvalidFields.delete(fieldName);
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  // Function to check if a field is invalid
  const isFieldInvalid = (fieldName: string) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      if (invalidFields.has(fieldName)) {
        return true;
      }
      for (const tab of tabs) {
        if (invalidFields.has(`${tab.step}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  // Generate embed code
  const generateEmbedCode = (): string => {
    if (!elementID) return '';
    return `<div data-entity="FUNDRAISING_BAR" data-source="CharityStack" id="${elementID}"></div>`;
  };

  // Copy embed code to clipboard
  const copyEmbedCode = async (): Promise<void> => {
    if (!canDistribute || !(await verifyDistribution())) return;
    const code = generateEmbedCode();
    if (code) {
      const success = await copyToClipboard(code);
      if (success) {
        toast.success('Embed code copied to clipboard');
      } else {
        toast.error('Failed to copy embed code');
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    const newInvalidFields = new Set<string>();

    if (!formData.elementName.trim()) {
      newInvalidFields.add('configuration-elementName');
    }
    if (formData.connectedForms.length === 0) {
      newInvalidFields.add('configuration-connectedForms');
    }
    if (formData.goal && isNaN(Number(formData.goal))) {
      newInvalidFields.add('configuration-goal');
    }
    if (formData.elementOffset && isNaN(Number(formData.elementOffset))) {
      newInvalidFields.add('configuration-elementOffset');
    }
    if (!formData.color || !formData.color.trim()) {
      newInvalidFields.add('appearance-color');
    }

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);
      focusFirstInvalidElement(rightPanelRef.current);
      toast.error('Please fix validation errors');
      return;
    }

    setIsSaving(true);

    try {
      const payload: any = {
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        CSMerchantID: props.merchant?.organizationId || '',
        color: formData.color,
        elementName: formData.elementName,
        elementType: 'FUNDRAISING_BAR',
        goal: formData.goal ? Number(formData.goal) : undefined,
        elementOffset: formData.elementOffset ? Number(formData.elementOffset) : undefined,
        trackingMetric: formData.trackingMetric,
        metricLabel: formData.metricLabel,
        formNames: formData.connectedForms,
        formIDs: formData.connectedForms
          .map((formName) => availableForms.find((form) => form.formName === formName)?.formID)
          .filter((id) => id !== undefined),
        active: publicationStateForSave({ existingId: elementID, active, paymentsReady }),
        ...(!elementID && {
          timeCreated: new Date().toISOString(),
          createdBy: props.user?.email || 'unknown',
        }),
      };

      if (elementID) {
        payload.elementID = elementID;
        delete payload.active;
      }

      const method = elementID ? 'PATCH' : 'POST';
      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          toast.error(data.error);
        } catch {
          throw new Error('API request failed');
        }
        return;
      }

      const data = await response.json();

      toast.success(
        elementID
          ? 'Element updated successfully'
          : paymentsReady
            ? 'Element published successfully'
            : 'Draft saved successfully',
      );
      if (!elementID) {
        setActive(data.active ?? payload.active);
        setElementID(data.elementID);
      }
      setFormsRaisedAmount(
        data.raised
          ? Number(data.raised) - (formData.elementOffset ? parseFloat(formData.elementOffset) : 0)
          : 0,
      );
      if (props.onSuccess) {
        props.onSuccess(data);
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      toast.error(`Failed to ${elementID ? 'update' : 'publish'} element`, {
        description: error?.message || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Activate/Deactivate element
  const activateElement = async (): Promise<void> => {
    setIsActivating(true);
    const state = !active;
    if (state && !paymentsReady) {
      setIsActivating(false);
      toast.info('Enable payments before publishing this draft.');
      return;
    }
    const successAlertMessage = state ? 'Element Activated' : 'Element Deactivated';

    try {
      const payload = {
        elementID,
        nonprofitName: props.merchant?.merchantName || 'Unknown Merchant',
        active: state,
      };

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(payload),
      };

      const url = `${import.meta.env.VITE_APP_ELEMENTS_URL}/elements/toggle-active`;
      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      } else {
        toast.success(successAlertMessage);
        setActive(state);
        if (props.onSuccess) props.onSuccess(data);
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Failed to update element status');
    } finally {
      setIsActivating(false);
    }
  };

  // Fetch forms
  useEffect(() => {
    const fetchForms = async (): Promise<void> => {
      if (!props.open) return;

      try {
        const merchants = await getAuthGroups();

        const response = await portalClient.postFormAndFundData({
          authorization: await getAuthToken(),
          body: {
            queryStringParameters: {
              nonprofit: merchants[0],
            },
          },
        });

        const data = await response.json();
        setAvailableForms(data.formNames);
      } catch (error) {
        console.error('Error fetching forms:', error);
        setAvailableForms([]);
      }
    };

    fetchForms();
  }, [props.open]);

  // Populate form data for edit mode
  useEffect(() => {
    if (props.elementData && props.open) {
      const data = props.elementData;

      setElementID(data.elementID);
      setFormData({
        elementName: data.elementName,
        connectedForms: data.formNames || [],
        color: data.color,
        goal: data.goal ? String(data.goal) : '',
        elementOffset: data.elementOffset ? String(data.elementOffset) : '',
        trackingMetric: data.trackingMetric || 'DOLLAR_AMOUNT',
        metricLabel: data.metricLabel || 'Sustainers',
      });
      setFormsRaisedAmount(data.raised ? Number(data.raised) - Number(data.elementOffset) : 0);
      setFormsSubscriptionCount(
        data.subscriptionCount
          ? Number(data.subscriptionCount) - Number(data.elementOffset || 0)
          : 0,
      );
      setFormsProductCount(
        data.productCount ? Number(data.productCount) - Number(data.elementOffset || 0) : 0,
      );
      setFormsDonationCount(
        data.donationCount ? Number(data.donationCount) - Number(data.elementOffset || 0) : 0,
      );
      setActive(data.active);
      setCurrentStep('configuration');
    }
  }, [props.elementData, props.open]);

  // Pre-fill the element color from the org's brand color when the drawer opens.
  // Only applies when (a) the drawer is open, (b) the org has a brand color set, and
  // (c) the color field is still untouched (empty) — so a color the user has picked
  // or one loaded from an existing element in edit mode is never overwritten.
  useEffect(() => {
    if (!props.open) return;
    const orgBrandColor = props.merchant?.brandColor;
    if (!orgBrandColor) return;
    setFormData((prev) => {
      if (prev.color) return prev; // user picked one, or edit-mode loaded one
      return { ...prev, color: orgBrandColor };
    });
  }, [props.open, props.merchant?.brandColor]);

  // Recalculate formsRaisedAmount when connected forms change
  useEffect(() => {
    // Don't reset to 0 while availableForms is still loading — keep the edit-mode value
    if (availableForms.length === 0) return;
    if (formData.connectedForms.length === 0) {
      setFormsRaisedAmount(0);
      setFormsSubscriptionCount(0);
      setFormsProductCount(0);
      setFormsDonationCount(0);
      return;
    }
    const total = formData.connectedForms.reduce((sum, formName) => {
      const form = availableForms.find((f) => f.formName === formName);
      return sum + (form?.totalTransactionAmount ? Number(form.totalTransactionAmount) : 0);
    }, 0);
    setFormsRaisedAmount(total);
  }, [formData.connectedForms, availableForms]);

  const clearStates = (): void => {
    setFormData(initialFormData);
    setElementID(null);
    setAvailableForms([]);
    setInvalidFields(new Set());
    setCurrentStep('configuration');
    setSketchPickerColor({ r: 0, g: 0, b: 0, a: 1 });
    setDisplayColorPicker(false);
    setActive(true);
    setIsSaving(false);
    setIsActivating(false);
    setFormsRaisedAmount(0);
    setFormsSubscriptionCount(0);
    setFormsProductCount(0);
    setFormsDonationCount(0);

    if (formRef.current) formRef.current.reset();
  };

  const handleSubmitLatest = useLatestCallback(handleSubmit);
  const activateElementLatest = useLatestCallback(activateElement);

  const adjustBrightness = (hexColor: string, percent: number): string => {
    if (!hexColor) return '#9A9285';

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const adjust = (color: number) => {
      const adjusted = color + Math.round((255 - color) * (percent / 100));
      return Math.max(0, Math.min(255, adjusted));
    };

    const newR = adjust(r);
    const newG = adjust(g);
    const newB = adjust(b);

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB
      .toString(16)
      .padStart(2, '0')}`;
  };

  const effectiveColor = formData.color || '#9A9285';

  // Generate brand colors: filled bar uses selected/default color, background is light gray
  const brandColors = {
    'accent-50': effectiveColor, // Filled bar and text color
    'accent-60': adjustBrightness(effectiveColor, -20),
    'accent-40': adjustBrightness(effectiveColor, 20),
    'accent-20': adjustBrightness(effectiveColor, 40),
    'accent-10': '#F7F8FA', // Unfilled background color (light gray)
  };

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (elementID) {
      actions.push(
        {
          key: 'save',
          label: isSaving ? 'Saving...' : 'Save',
          variant: 'primary' as const,
          disabled: isSaving || isActivating,
          onClick: () => handleSubmitLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Activate'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isActivating || isSaving || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => activateElementLatest(),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isSaving || isActivating,
        variant: 'primary' as const,
        onClick: () => handleSubmitLatest(),
      });
    }

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isSaving,
    isActivating,
    elementID,
    active,
    paymentsReady,
    handleSubmitLatest,
    activateElementLatest,
  ]);

  // Don't render if required props are missing
  if (!props.merchant) {
    return null;
  }

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        props.setOpen(open);
        if (!open) clearStates();
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        className={
          props.presentation === 'workspace'
            ? 'min-h-full overflow-visible rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
        onEscapeKeyDown={() => clearStates()}
        onPointerDownOutside={() => clearStates()}
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full max-w-[1600px] ${
            props.presentation === 'workspace' ? 'px-3 py-2 md:py-3' : 'h-full pointer-events-none'
          }`}
        >
          {/* Header with Action Buttons */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start gap-x-[6px]">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-6 w-6"
                  >
                    <path d="M3 3h18v4H3V3zm0 6h18v4H3V9zm0 6h14v4H3v-4z" fill="#6B6458" />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {elementID ? 'Edit Fundraising Bar' : 'Create Fundraising Bar'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    Create a progress bar to track fundraising goals
                  </p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    disabled={isSaving || isActivating}
                    variant="primary"
                    size="workspace"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                )}

                {/* Activate/Deactivate Button - Only when editing */}
                {elementID && (
                  <Button
                    onClick={() => activateElement()}
                    disabled={isActivating || isSaving || (!active && !paymentsReady)}
                    variant={!active ? 'primary' : 'danger'}
                    size="workspace"
                  >
                    {!active
                      ? isActivating
                        ? 'Activating...'
                        : 'Activate'
                      : isActivating
                        ? 'Deactivating...'
                        : 'Deactivate'}
                  </Button>
                )}

                {/* Publish Button - Only when creating */}
                {!elementID && (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    size="workspace"
                    disabled={isSaving || isActivating}
                  >
                    {isSaving ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}

          {/* Main Content - Split Layout */}
          <div
            className={`pointer-events-auto flex w-full flex-col-reverse gap-6 lg:flex-row ${
              props.presentation === 'workspace'
                ? 'overflow-visible'
                : 'h-[calc(100%-80px)] overflow-y-auto md:overflow-hidden'
            }`}
          >
            {/* Left Side - Preview Section */}
            <div
              className={`flex flex-col gap-4 pr-2 md:max-h-[150px] lg:w-1/2 ${
                useCreateWorkspaceCards
                  ? 'overflow-visible'
                  : 'h-full overflow-y-auto md:min-h-full'
              } ${elementID ? 'min-h-[150px] max-h-[200px]' : 'min-h-[80px] max-h-[100px]'}`}
            >
              {/* CON-42: this frame is dashboard chrome; its contents are a donor
                  component, which would otherwise resolve every --cs-* against
                  dashboard.css. See decisions.md D-previewer-scope. */}
              <div
                data-cs-position="donor"
                className="flex w-full flex-col items-stretch rounded-md border border-edge bg-paper-muted px-4 py-4"
              >
                <FundraisingBar
                  fundraisingBarID="NEW_FORM"
                  brandColors={brandColors}
                  amounts={{
                    targetAmount: formData.goal ? parseFloat(formData.goal) : 10000000,
                    raisedAmount:
                      formsRaisedAmount +
                      (formData.trackingMetric === 'DOLLAR_AMOUNT'
                        ? formData.elementOffset
                          ? parseFloat(formData.elementOffset)
                          : 0
                        : 0),
                    trackingMetric: formData.trackingMetric,
                    subscriptionCount:
                      formsSubscriptionCount +
                      (formData.trackingMetric === 'SUBSCRIPTION_COUNT'
                        ? formData.elementOffset
                          ? parseFloat(formData.elementOffset)
                          : 0
                        : 0),
                    productCount:
                      formsProductCount +
                      (formData.trackingMetric === 'PRODUCT_COUNT'
                        ? formData.elementOffset
                          ? parseFloat(formData.elementOffset)
                          : 0
                        : 0),
                    donationCount:
                      formsDonationCount +
                      (formData.trackingMetric === 'DONATION_COUNT'
                        ? formData.elementOffset
                          ? parseFloat(formData.elementOffset)
                          : 0
                        : 0),
                    metricLabel: formData.metricLabel,
                    elementOffset: formData.elementOffset ? parseFloat(formData.elementOffset) : 0,
                  }}
                  previewMode={true}
                />
              </div>
              {elementID && canDistribute && (
                <div className="mt-4">
                  <Label className="mb-2 text-sm font-medium text-ink">Embed Code</Label>
                  <div className="relative w-full">
                    <textarea
                      value={generateEmbedCode()}
                      rows={1}
                      className="block h-14 w-full resize-none rounded-md border border-edge-input bg-paper-inset pr-20 text-xs shadow-sm"
                      readOnly
                      onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-3 flex items-center justify-center rounded-md p-1.5 text-ink-secondary hover:bg-paper-inset hover:text-ink"
                      onClick={copyEmbedCode}
                      title="Copy Embed Code"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={rightPanelRef}
              className={`compact-editor-controls flex flex-col lg:w-1/2 lg:pl-4 ${
                useCreateWorkspaceCards
                  ? 'min-h-0 overflow-visible pb-0'
                  : 'h-full overflow-hidden pb-14 md:min-h-full md:pb-0'
              }`}
            >
              {/* Form Tabs */}
              {!useCreateWorkspaceCards && (
                <div className="border-edge bg-white">
                  <nav
                    className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                    aria-label="Tabs"
                  >
                    {tabs.map((tab) => {
                      // Check if this tab has errors by looking for fields with the tab's step prefix
                      const tabHasErrors = Array.from(invalidFields).some((field) =>
                        field.startsWith(tab.step + '-'),
                      );
                      return (
                        <button
                          type="button"
                          key={tab.name}
                          onClick={() => setCurrentStep(tab.step)}
                          className={classNames(
                            currentStep === tab.step
                              ? tabHasErrors
                                ? 'border-red-500 text-red-600'
                                : 'border-ink-secondary text-ink-secondary'
                              : tabHasErrors
                                ? 'border-transparent text-red-500 hover:text-red-600'
                                : 'border-transparent text-ink-muted hover:text-ink-secondary',
                            'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                          )}
                        >
                          {tab.name}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* Form Content */}
              <div
                className={`flex-1 ${
                  useCreateWorkspaceCards ? 'space-y-4 overflow-visible' : 'overflow-y-auto py-4'
                }`}
              >
                {/* Configuration Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'configuration'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-4'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Configuration"
                    description="Choose the metric, goal, connected forms, and starting offset."
                    icon={elementEditorSectionIcons.configuration}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'configuration')}
                  >
                    <FundraisingBarConfiguration
                      formData={formData}
                      updateFormData={updateFormData}
                      availableForms={availableForms}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                    />
                  </ElementEditorSection>
                </div>

                {/* Appearance Section */}
                <div
                  className={
                    useCreateWorkspaceCards || currentStep === 'appearance'
                      ? useCreateWorkspaceCards
                        ? ''
                        : 'space-y-4'
                      : 'hidden'
                  }
                >
                  <ElementEditorSection
                    enabled={useCreateWorkspaceCards}
                    title="Appearance"
                    description="Set the fundraising bar color and visual treatment."
                    icon={elementEditorSectionIcons.appearance}
                    hasErrors={elementEditorSectionHasErrors(invalidFields, 'appearance')}
                  >
                    <AppearanceTab
                      color={formData.color}
                      setColor={(value) => updateFormData('color', value)}
                      sketchPickerColor={sketchPickerColor}
                      setSketchPickerColor={setSketchPickerColor}
                      displayColorPicker={displayColorPicker}
                      setDisplayColorPicker={setDisplayColorPicker}
                      handleFieldFocus={handleFieldFocus}
                      isFieldInvalid={isFieldInvalid}
                      invalidFields={invalidFields}
                      setInvalidFields={setInvalidFields}
                      elementType="fundraise-bar"
                    />
                  </ElementEditorSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
